/**
 * Route metadata without schemas: what an HTTP client needs (method, path, auth, CSRF, idempotency).
 * TypeBox-free so the browser bundle stays small. Kept equal to `routes` by contract.test.ts;
 * regenerate with `pnpm --filter @checkone/contract gen:meta` after changing a route.
 */
import type { RouteMetaShape } from "./route-meta-shape.js";
import type { RouteName } from "./routes.js";

export const routeMeta = {
  "public.check": {
    method: "GET",
    path: "/v1/public/check",
    auth: "public",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "public.share": {
    method: "GET",
    path: "/v1/public/share/:token",
    auth: "public",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "auth.magicLink": {
    method: "POST",
    path: "/v1/auth/magic-link",
    auth: "public",
    status: 202,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "auth.verify": {
    method: "POST",
    path: "/v1/auth/verify",
    auth: "public",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "auth.logout": {
    method: "POST",
    path: "/v1/auth/logout",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "auth.me": {
    method: "GET",
    path: "/v1/auth/me",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "auth.phoneOtp": {
    method: "POST",
    path: "/v1/auth/phone/otp",
    auth: "session",
    status: 202,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "auth.phoneVerify": {
    method: "POST",
    path: "/v1/auth/phone/verify",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "tenant.get": {
    method: "GET",
    path: "/v1/tenant",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "tenant.patch": {
    method: "PATCH",
    path: "/v1/tenant",
    auth: "owner",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "tenant.delete": {
    method: "DELETE",
    path: "/v1/tenant",
    auth: "owner",
    status: 202,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "tenant.practitionerRequest": {
    method: "POST",
    path: "/v1/tenant/practitioner-request",
    auth: "owner",
    status: 202,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "tenant.channels.patch": {
    method: "PATCH",
    path: "/v1/tenant/channels",
    auth: "member",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "tenant.users.list": {
    method: "GET",
    path: "/v1/tenant/users",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "tenant.users.invite": {
    method: "POST",
    path: "/v1/tenant/users/invite",
    auth: "owner",
    status: 201,
    idempotent: true,
    csrf: true,
    produces: "application/json",
  },
  "tenant.users.patch": {
    method: "PATCH",
    path: "/v1/tenant/users/:id",
    auth: "owner",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "tenant.users.remove": {
    method: "DELETE",
    path: "/v1/tenant/users/:id",
    auth: "owner",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "tenant.invites.list": {
    method: "GET",
    path: "/v1/tenant/invites",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "tenant.invites.revoke": {
    method: "DELETE",
    path: "/v1/tenant/invites/:id",
    auth: "owner",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "clients.list": {
    method: "GET",
    path: "/v1/clients",
    auth: "practitioner",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "clients.create": {
    method: "POST",
    path: "/v1/clients",
    auth: "practitioner",
    status: 201,
    idempotent: true,
    csrf: true,
    produces: "application/json",
  },
  "clients.switch": {
    method: "POST",
    path: "/v1/clients/:id/switch",
    auth: "practitioner",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "clients.branding": {
    method: "PATCH",
    path: "/v1/clients/:id/branding",
    auth: "practitioner",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "practiceWatch.get": {
    method: "GET",
    path: "/v1/practice-watch",
    auth: "practitioner",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "practiceWatch.roster.add": {
    method: "POST",
    path: "/v1/practice-watch/roster",
    auth: "practitioner",
    status: 201,
    idempotent: true,
    csrf: true,
    produces: "application/json",
  },
  "practiceWatch.roster.remove": {
    method: "DELETE",
    path: "/v1/practice-watch/roster/:id",
    auth: "practitioner",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "vendors.list": {
    method: "GET",
    path: "/v1/vendors",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "vendors.get": {
    method: "GET",
    path: "/v1/vendors/:id",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "vendors.create": {
    method: "POST",
    path: "/v1/vendors",
    auth: "member",
    status: 201,
    idempotent: true,
    csrf: true,
    produces: "application/json",
  },
  "vendors.patch": {
    method: "PATCH",
    path: "/v1/vendors/:id",
    auth: "member",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "vendors.delete": {
    method: "DELETE",
    path: "/v1/vendors/:id",
    auth: "member",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "vendors.suggestions": {
    method: "GET",
    path: "/v1/vendors/:id/suggestions",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "vendors.history": {
    method: "GET",
    path: "/v1/vendors/:id/history",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "vendors.statusOn": {
    method: "GET",
    path: "/v1/vendors/:id/status-on",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "uploads.create": {
    method: "POST",
    path: "/v1/uploads",
    auth: "member",
    status: 201,
    idempotent: true,
    csrf: true,
    produces: "application/json",
  },
  "uploads.get": {
    method: "GET",
    path: "/v1/uploads/:id",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "uploads.mapping": {
    method: "POST",
    path: "/v1/uploads/:id/mapping",
    auth: "member",
    status: 202,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "uploads.preview": {
    method: "GET",
    path: "/v1/uploads/:id/preview",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "uploads.commit": {
    method: "POST",
    path: "/v1/uploads/:id/commit",
    auth: "member",
    status: 202,
    idempotent: true,
    csrf: true,
    produces: "application/json",
  },
  "runs.list": {
    method: "GET",
    path: "/v1/runs",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "runs.get": {
    method: "GET",
    path: "/v1/runs/:id",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "runs.events": {
    method: "GET",
    path: "/v1/runs/:id/events",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "runs.pdf": {
    method: "GET",
    path: "/v1/runs/:id/pdf",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/pdf",
  },
  "runPages.get": {
    method: "GET",
    path: "/v1/run-pages/:token",
    auth: "signed_token",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "changes.ack": {
    method: "POST",
    path: "/v1/changes/:id/ack",
    auth: "member",
    status: 201,
    idempotent: true,
    csrf: true,
    produces: "application/json",
  },
  "changes.unactioned": {
    method: "GET",
    path: "/v1/changes/unactioned",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "exports.list": {
    method: "GET",
    path: "/v1/exports",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "exports.create": {
    method: "POST",
    path: "/v1/exports",
    auth: "session",
    status: 202,
    idempotent: true,
    csrf: true,
    produces: "application/json",
  },
  "exports.get": {
    method: "GET",
    path: "/v1/exports/:id",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "paymentChecks.list": {
    method: "GET",
    path: "/v1/payment-checks",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "paymentChecks.read": {
    method: "POST",
    path: "/v1/payment-checks/read",
    auth: "member",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "paymentChecks.readText": {
    method: "POST",
    path: "/v1/payment-checks/read-text",
    auth: "member",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "paymentChecks.create": {
    method: "POST",
    path: "/v1/payment-checks",
    auth: "member",
    status: 201,
    idempotent: true,
    csrf: true,
    produces: "application/json",
  },
  "paymentChecks.get": {
    method: "GET",
    path: "/v1/payment-checks/:id",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "billing.summary": {
    method: "GET",
    path: "/v1/billing",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "billing.plans": {
    method: "GET",
    path: "/v1/billing/plans",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "billing.checkout": {
    method: "POST",
    path: "/v1/billing/checkout",
    auth: "owner",
    status: 201,
    idempotent: true,
    csrf: true,
    produces: "application/json",
  },
  "billing.bankTransfer": {
    method: "POST",
    path: "/v1/billing/bank-transfer",
    auth: "owner",
    status: 201,
    idempotent: true,
    csrf: true,
    produces: "application/json",
  },
  "billing.invoices": {
    method: "GET",
    path: "/v1/billing/invoices",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "messages.list": {
    method: "GET",
    path: "/v1/messages",
    auth: "session",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "webhooks.gateway": {
    method: "POST",
    path: "/v1/webhooks/gateway",
    auth: "webhook",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "webhooks.whatsapp": {
    method: "POST",
    path: "/v1/webhooks/whatsapp",
    auth: "webhook",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "webhooks.email": {
    method: "POST",
    path: "/v1/webhooks/email",
    auth: "webhook",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "webhooks.formone": {
    method: "POST",
    path: "/v1/webhooks/formone",
    auth: "webhook",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "admin.sources.list": {
    method: "GET",
    path: "/v1/admin/sources",
    auth: "admin",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "admin.sources.quarantine": {
    method: "POST",
    path: "/v1/admin/sources/:id/quarantine",
    auth: "admin",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "admin.sources.release": {
    method: "POST",
    path: "/v1/admin/sources/:id/release",
    auth: "admin",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "admin.rates.list": {
    method: "GET",
    path: "/v1/admin/rates",
    auth: "admin",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "admin.rates.propose": {
    method: "POST",
    path: "/v1/admin/rates",
    auth: "admin",
    status: 201,
    idempotent: true,
    csrf: true,
    produces: "application/json",
  },
  "admin.rates.approve": {
    method: "POST",
    path: "/v1/admin/rates/:version/approve",
    auth: "admin",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "admin.flags.list": {
    method: "GET",
    path: "/v1/admin/flags",
    auth: "admin",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "admin.flags.set": {
    method: "PUT",
    path: "/v1/admin/flags/:key",
    auth: "admin",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "admin.tenants.search": {
    method: "GET",
    path: "/v1/admin/tenants",
    auth: "admin",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "admin.tenants.setKind": {
    method: "PUT",
    path: "/v1/admin/tenants/:id/kind",
    auth: "admin",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "admin.messages.list": {
    method: "GET",
    path: "/v1/admin/messages",
    auth: "admin",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "admin.runs.list": {
    method: "GET",
    path: "/v1/admin/runs",
    auth: "admin",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "admin.runs.replay": {
    method: "POST",
    path: "/v1/admin/runs/:id/replay",
    auth: "admin",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
  "admin.invoices.list": {
    method: "GET",
    path: "/v1/admin/invoices",
    auth: "admin",
    status: 200,
    idempotent: false,
    csrf: false,
    produces: "application/json",
  },
  "admin.invoices.markPaid": {
    method: "POST",
    path: "/v1/admin/invoices/:id/mark-paid",
    auth: "admin",
    status: 200,
    idempotent: false,
    csrf: true,
    produces: "application/json",
  },
} as const satisfies Record<RouteName, RouteMetaShape>;
